import { useSessionStore } from '../auth/session-store';

const API_PREFIX = '/api/v1';

/** Asking these to refresh would be asking a failed refresh to refresh itself. */
const NEVER_REFRESHED = [
  '/auth/refresh',
  '/auth/login',
  '/auth/logout',
  '/auth/password-reset',
  '/auth/password-reset/confirm',
];

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RefreshResponse {
  userId: string;
  accessToken?: string;
  mustEnrollTotp?: boolean;
}

/**
 * One request, with the access token attached and one silent renewal on 401.
 *
 * `credentials: 'include'` is not optional: the refresh token lives in an
 * httpOnly cookie, and the browser only attaches it when credentials are asked
 * for. Omitting it makes every silent refresh fail for a reason that is
 * invisible in the code.
 */
export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await send(path, init);

  if (response.status === 401 && !NEVER_REFRESHED.includes(path)) {
    const renewed = await refreshSession();

    if (!renewed) {
      useSessionStore.getState().clear();
      throw await toApiError(response);
    }

    // Exactly one retry, never a loop. A session that is genuinely over would
    // otherwise spin against the server while the user watches a frozen page.
    const retried = await send(path, init);

    if (!retried.ok) {
      throw await toApiError(retried);
    }

    return parse<T>(retried);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return parse<T>(response);
}

function send(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = useSessionStore.getState().session?.accessToken;

  if (token !== undefined) {
    headers.set('authorization', `Bearer ${token}`);
  }

  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  return fetch(`${API_PREFIX}${path}`, { ...init, headers, credentials: 'include' });
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Renews the session from the refresh cookie, once, no matter how many callers
 * ask at the same time.
 *
 * The refresh token rotates on every use. On a reload the session restore and
 * every screen's first request all reach for it together; a second refresh
 * would present the token the first one just rotated away, and the server
 * rejects it. Every caller joins the refresh already in flight instead.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= renewSession().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function renewSession(): Promise<boolean> {
  // The cookie travels on its own; there is nothing to put in the body.
  const response = await send('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    return false;
  }

  const refreshed = (await response.json()) as RefreshResponse;
  const current = useSessionStore.getState().session;

  useSessionStore.getState().signIn({
    userId: refreshed.userId,
    accessToken: refreshed.accessToken ?? current?.accessToken ?? '',
    accessTokenExpiresAt: current?.accessTokenExpiresAt ?? new Date(),
    mustEnrollTotp: refreshed.mustEnrollTotp ?? current?.mustEnrollTotp ?? false,
  });

  return true;
}

async function parse<T>(response: Response): Promise<T> {
  // 202 is the password-reset request: accepted, deliberately without a body.
  if (response.status === 204 || response.status === 202) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as { message?: string };

    return new ApiError(response.status, body.message ?? response.statusText, body);
  } catch {
    // A gateway answering with HTML is still a failure worth reporting, not a
    // parse error thrown from inside the error path.
    return new ApiError(response.status, response.statusText);
  }
}
